import React, { useState, useEffect } from 'react';
import {
	Elements,
	StripeProvider,
	CardNumberElement,
	CardExpiryElement,
	CardCvcElement,
	injectStripe,
} from 'react-stripe-elements';
import alipay from 'payment-icons/min/flat/alipay.svg';
import amex from 'payment-icons/min/flat/amex.svg';
import defaultflat from 'payment-icons/min/flat/default.svg';
import defaultmono from 'payment-icons/min/mono/default.svg';
import diners from 'payment-icons/min/flat/diners.svg';
import discover from 'payment-icons/min/flat/discover.svg';
import elo from 'payment-icons/min/flat/elo.svg';
import hipercard from 'payment-icons/min/flat/hipercard.svg';
import jcb from 'payment-icons/min/flat/jcb.svg';
import maestro from 'payment-icons/min/flat/maestro.svg';
import mastercard from 'payment-icons/min/flat/mastercard.svg';
import paypal from 'payment-icons/min/flat/paypal.svg';
import unionpay from 'payment-icons/min/flat/unionpay.svg';
import verve from 'payment-icons/min/flat/verve.svg';
import visa from 'payment-icons/min/flat/visa.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import Collapse from '@kunukn/react-collapse';
import Skeleton from 'react-loading-skeleton';
import LoadingOverlay from 'react-loading-overlay';

import AlertModal from '../shared/AlertModal';
import ConfirmModal from '../shared/ConfirmModal';

const cardLogos = {
	alipay,
	amex,
	diners,
	discover,
	elo,
	hipercard,
	jcb,
	maestro,
	mastercard,
	paypal,
	unionpay,
	verve,
	visa,
};

const capitalize = (str) => {
	return str.charAt(0).toUpperCase() + str.slice(1);
};

const PaymentMethods = ({ stripePublishableKey }) => {
	const [isLoading, setIsLoading] = useState(true);
	const [activePm, setActivePm] = useState(0);
	const [paymentMethods, setPaymentMethods] = useState([]);
	const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);
	const [invoices, setInvoices] = useState([]);
	const [alertMessage, setAlertMessage] = useState(null);

	useEffect(() => {
		loadPaymentMethods();
	}, []);

	useEffect(() => {
		loadInvoices();
	}, []);

	const loadPaymentMethods = async () => {
		const response = await fetch('/api/payment_methods', {
			method: 'GET',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
		});
		const { payment_methods } = await response.json();
		setPaymentMethods(payment_methods);
		setIsLoading(false);
	};

	const updatePaymentMethod = (updatedPm) => {
		setPaymentMethods(
			paymentMethods.map((pm) => (pm.id == updatedPm.id ? updatedPm : pm))
		);
	};

	const prependPaymentMethod = (newPm) => {
		setPaymentMethods([newPm, ...paymentMethods]);
	};

	const loadInvoices = async () => {
		const response = await fetch('/api/invoices', {
			method: 'GET',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
		});
		const { invoices } = await response.json();
		setInvoices(invoices);
		setIsLoadingInvoices(false);
	};

	const showSuccess = (msg) => {
		setAlertMessage(<div className="color-primary">{msg}</div>);
	};

	const showError = (msg) => {
		setAlertMessage(<div className="color-alert">{msg}</div>);
	};

	return (
		<React.Fragment>
			<div id="payment-methods" className="row">
				<div className="columns small-12 medium-8 medium-offset-2">
					<h1>Payment methods</h1>
					<br />
					<div className="row">
						<div className="columns small-6">
							<p>
								<b>Your credit cards</b>
							</p>
						</div>
						<div className="columns small-6">
							<p>
								<b>Expires on</b>
							</p>
						</div>
					</div>
					{isLoading ? (
						<SkeletonLoading />
					) : (
						paymentMethods.map((pm) => (
							<PaymentMethodItem
								key={pm.id}
								{...{
									pm,
									activePm,
									setActivePm,
									loadPaymentMethods,
									updatePaymentMethod,
									showError,
								}}
							/>
						))
					)}
					{!isLoading && !paymentMethods.length && <Empty />}
					<br />
					<StripeProvider apiKey={stripePublishableKey}>
						<Elements>
							<PaymentMethodForm
								{...{
									prependPaymentMethod,
									showSuccess,
									showError,
								}}
							/>
						</Elements>
					</StripeProvider>
				</div>
			</div>
			<div id="payment-history" className="row">
				<div className="columns small-12 medium-8 medium-offset-2">
					<h1>Payment history</h1>
					<br />
					<div className="card mb-5 payment-history-card">
						{isLoadingInvoices && <SkeletonLoading />}
						{!isLoadingInvoices && !invoices.length && 'No Data'}
						{!isLoadingInvoices &&
							invoices.map((i) => (
								<InvoiceItem key={i.id} data={i} />
							))}
					</div>
				</div>
			</div>
			<AlertModal onClose={() => setAlertMessage(null)}>
				{alertMessage}
			</AlertModal>
		</React.Fragment>
	);
};

const SkeletonLoading = () => (
	<div className="card payment-methods-card">
		<div className="row">
			<div className="columns small-6">
				<img src={defaultmono} />
				<Skeleton width={210} height={20} />
			</div>
			<div className="columns small-6">
				<Skeleton width={80} height={20} />
				<span className="arrow-down">
					<Skeleton width={20} height={20} />
				</span>
			</div>
		</div>
	</div>
);

const Empty = () => (
	<div className="card payment-methods-card color-secondary">
		You have not saved any payment methods yet
	</div>
);

const loadingOverlayStyle = {
	spinner: (base) => ({
		...base,
		'& svg circle': {
			stroke: '#1779ba',
			strokeWidth: 3,
		},
	}),
};

const PaymentMethodItem = ({
	pm,
	activePm,
	setActivePm,
	loadPaymentMethods,
	updatePaymentMethod,
	showError,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [form, setForm] = useState({ address: {} });
	const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

	useEffect(() => {
		if (isEditing) {
			setForm(JSON.parse(JSON.stringify(pm.billing_details)));
		}
	}, [isEditing]);

	const remove = async () => {
		setIsConfirmDeleteOpen(false);
		setIsLoading(true);
		const response = await fetch(`/api/payment_methods/${pm.id}`, {
			method: 'DELETE',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
		});
		const { error } = await response.json();
		if (error) {
			showError(error);
			setIsLoading(false);
		} else {
			loadPaymentMethods();
		}
	};

	const edit = () => {
		setIsEditing(true);
	};

	const handleChange = (e) => {
		setForm({ ...form, [e.target.name]: e.target.value });
	};

	const handleAddressChange = (e) => {
		setForm({
			...form,
			address: { ...form.address, [e.target.name]: e.target.value },
		});
	};

	const submit = async (e) => {
		e.preventDefault();
		setIsLoading(true);
		const response = await fetch(`/api/payment_methods/${pm.id}`, {
			method: 'PUT',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ payment_method: { billing_details: form } }),
		});
		const { payment_method, error } = await response.json();
		if (error) {
			showError(error);
			setIsLoading(false);
		} else {
			updatePaymentMethod(payment_method);
			setIsLoading(false);
			setIsEditing(false);
		}
	};

	return (
		<React.Fragment>
			<LoadingOverlay
				active={isLoading && pm.id == activePm}
				spinner
				styles={loadingOverlayStyle}
			>
				<div className="card payment-methods-card">
					<div
						className="row pointer"
						onClick={() =>
							setActivePm(pm.id == activePm ? 0 : pm.id)
						}
					>
						<div className="columns small-6">
							<img
								src={cardLogos[pm.card.brand] || defaultflat}
							/>
							{capitalize(pm.card.brand)} ending in{' '}
							{pm.card.last4}
						</div>
						<div className="columns small-6">
							{pm.card.exp_month}/{pm.card.exp_year}
							<span
								className={`arrow-down ${
									pm.id == activePm && 'animate-rotate180'
								}`}
							>
								<FontAwesomeIcon icon={faAngleDown} />
							</span>
						</div>
					</div>
					<Collapse isOpen={activePm == pm.id}>
						<br />
						{!isEditing && (
							<React.Fragment>
								<div className="row">
									<div className="columns small-6">
										<h6>
											<b>Name on card</b>
										</h6>
										<div>{pm.billing_details.name}</div>
										<div>{pm.billing_details.email}</div>
										<div>{pm.billing_details.phone}</div>
									</div>
									<div className="columns small-6">
										<h6>
											<b>Billing address</b>
										</h6>
										<div>
											{pm.billing_details.address.line1}
										</div>
										<div>
											{pm.billing_details.address.line2}
										</div>
										<div>
											{[
												pm.billing_details.address.city,
												pm.billing_details.address
													.state,
												pm.billing_details.address
													.postal_code,
											]
												.filter((e) => e)
												.join(', ')}
										</div>
										<div>
											{pm.billing_details.address.country}
										</div>
									</div>
								</div>
								<br />
								<div className="text-right">
									<button
										className="primary-button button-sm right"
										onClick={edit}
									>
										<p>Edit </p>
									</button>
									&nbsp;
									<button
										className="secondary-button button-sm"
										onClick={() =>
											setIsConfirmDeleteOpen(true)
										}
									>
										<p>Remove</p>
									</button>
								</div>
							</React.Fragment>
						)}
						{isEditing && (
							<form onSubmit={submit}>
								<div className="form-inputs">
									<div className="row">
										<div className="columns small-6">
											<div className="input-wrapper">
												<label>
													Name on card
													<input
														name="name"
														value={form.name || ''}
														onChange={handleChange}
														required
													/>
												</label>
											</div>
											<div className="input-wrapper">
												<label>
													Email
													<input
														name="email"
														value={form.email || ''}
														onChange={handleChange}
													/>
												</label>
											</div>
											<div className="input-wrapper">
												<label>
													Phone
													<input
														name="phone"
														value={form.phone || ''}
														onChange={handleChange}
													/>
												</label>
											</div>
										</div>
										<div className="columns small-6">
											<div className="input-wrapper">
												<label>
													Address line 1
													<input
														name="line1"
														value={
															form.address
																.line1 || ''
														}
														onChange={
															handleAddressChange
														}
													/>
												</label>
											</div>
											<div className="input-wrapper">
												<label>
													Address line 2
													<input
														name="line2"
														value={
															form.address
																.line2 || ''
														}
														onChange={
															handleAddressChange
														}
													/>
												</label>
											</div>
											<div className="input-wrapper">
												<label>
													City
													<input
														name="city"
														value={
															form.address.city ||
															''
														}
														onChange={
															handleAddressChange
														}
													/>
												</label>
											</div>
											<div className="input-wrapper">
												<label>
													State
													<input
														name="state"
														value={
															form.address
																.state || ''
														}
														onChange={
															handleAddressChange
														}
													/>
												</label>
											</div>
											<div className="input-wrapper">
												<label>
													Postal Code
													<input
														name="postal_code"
														value={
															form.address
																.postal_code ||
															''
														}
														onChange={
															handleAddressChange
														}
													/>
												</label>
											</div>
											<div className="input-wrapper">
												<label>
													Country
													<input
														name="country"
														value={
															form.address
																.country || ''
														}
														onChange={
															handleAddressChange
														}
														placeholder="Use a 2-character alphanumeric country code"
													/>
												</label>
											</div>
										</div>
									</div>
								</div>
								<br />
								<div className="text-right">
									<button
										className="primary-button button-sm right"
										onClick={edit}
									>
										{' '}
										&nbsp; Edit &nbsp;{' '}
									</button>
									<button
										className="secondary-button button-sm"
										onClick={() =>
											setIsConfirmDeleteOpen(true)
										}
									>
										Remove
									</button>
								</div>
							</form>
						)}
					</Collapse>
				</div>
			</LoadingOverlay>
			<ConfirmModal
				isOpen={isConfirmDeleteOpen}
				onCancel={() => setIsConfirmDeleteOpen(false)}
				onProceed={remove}
			>
				Are you sure you want to remove payment method?
			</ConfirmModal>
		</React.Fragment>
	);
};

const inputStyle = {
	base: {
		color: 'rgba(0, 0, 0, 0.75)',
	},
};

const PaymentMethodForm = injectStripe(
	({ stripe, prependPaymentMethod, showSuccess, showError }) => {
		const [name, setName] = useState('');
		const [isSubmitting, setIsSubmitting] = useState(false);
		const [resetKey, setResetKey] = useState(1);

		const submit = async () => {
			if (!name) {
				showError('Name on card is required.');
				return;
			}
			setIsSubmitting(true);
			const { paymentMethod, error: paymentMethodError } =
				await stripe.createPaymentMethod('card', {
					billing_details: { name },
				});
			if (paymentMethodError) {
				showError(paymentMethodError.message);
				setIsSubmitting(false);
				return;
			}
			const response = await fetch(
				`/api/payment_methods/${paymentMethod.id}/setup_intent`,
				{
					method: 'POST',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			);
			const { setup_intent: siData, error: validationError } =
				await response.json();
			if (validationError) {
				showError(validationError);
				setIsSubmitting(false);
			} else {
				const { setupIntent, error: setupError } =
					await stripe.handleCardSetup(siData.client_secret, {
						payment_method_data: { billing_details: { name } },
					});
				if (setupError) {
					showError(setupError.message);
					setIsSubmitting(false);
				} else {
					const response = await fetch(
						`/api/payment_methods/${setupIntent.payment_method}/attach`,
						{
							method: 'POST',
							headers: {
								Accept: 'application/json',
								'Content-Type': 'application/json',
							},
						}
					);
					const { payment_method: newPm, error: attachError } =
						await response.json();
					if (attachError) {
						showError(attachError);
					} else {
						prependPaymentMethod(newPm);
						reset();
						showSuccess('Successfully added payment method');
					}
					setIsSubmitting(false);
				}
			}
		};

		const reset = () => {
			setName('');
			setResetKey(resetKey + 1);
		};

		return (
			<React.Fragment>
				<p>
					<b>Add a new payment method</b>
				</p>
				<LoadingOverlay
					active={isSubmitting}
					spinner
					styles={loadingOverlayStyle}
				>
					<div className="card mb-5">
						<p>Enter your card information</p>
						<form>
							<div className="form-inputs pb-3">
								<div className="input-wrapper pb-2">
									<label>
										<b>Name on card</b>
										<input
											value={name}
											onChange={(e) =>
												setName(e.target.value)
											}
											placeholder="John Doe"
										/>
									</label>
								</div>
								<div className="input-wrapper pb-2">
									<label>
										<b>Card number</b>
										<CardNumberElement
											key={resetKey}
											style={inputStyle}
										/>
									</label>
								</div>
								<div className="input-wrapper pb-2">
									<label>
										<b>Expiration date</b>
										<CardExpiryElement
											key={resetKey}
											style={inputStyle}
										/>
									</label>
								</div>
								<div className="input-wrapper pb-2">
									<label>
										<b>CVC</b>
										<CardCvcElement
											key={resetKey}
											style={inputStyle}
										/>
									</label>
								</div>
							</div>
							<div className="form-actions clearfix text-right">
								<button
									type="button"
									className="primary-button"
									onClick={submit}
								>
									<p>Add your card</p>
								</button>
							</div>
						</form>
					</div>
				</LoadingOverlay>
			</React.Fragment>
		);
	}
);

const InvoiceItem = ({ data }) => {
	const formatDate = (utcSeconds) => {
		let d = new Date(0);
		d.setUTCSeconds(utcSeconds);
		return d.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		});
	};

	return (
		<div style={{ display: 'flex' }}>
			<div style={{ flexGrow: 1 }}>
				{capitalize(data.status)}:{' '}
				<b>{data.lines.data[0].description}</b>,{' '}
				<span className="color-secondary">
					{formatDate(data.created)}
				</span>
			</div>
			<div>
				<a href={data.hosted_invoice_url} target="_blank">
					Invoice
				</a>
			</div>
		</div>
	);
};

export default PaymentMethods;
